import { proxy, subscribe } from "valtio";
import { User } from "@models/user.model";
import { LoginService } from "@services/login/loginService";
import { ImpersonationService } from "@services/impersonation/impersonationService";

// The old platform's token used to be kept beside the platform token (MPN-1977 retired it).
// Nothing reads it now; clear it so it does not sit in every browser indefinitely.
localStorage.removeItem("opToken");

const REAL_TOKEN_KEY = "impersonationRealToken";
const EXPIRES_KEY = "impersonationExpiresAt";
const DISPLAY_KEY = "impersonationDisplayName";

export const appStore = proxy<{
    loggedInUser: User;
    authToken: string;
    /** Display name of the impersonated user; null when not impersonating. */
    viewingAsUser: string | null;
    /** Original admin user, restored when impersonation stops. */
    realUser: User | null;
    /** Original admin Knox token, used to restore session on stop. */
    realToken: string;
    /** ISO timestamp when the impersonation token expires. */
    impersonationExpiresAt: string | null;
}>({
    loggedInUser: new User(),
    authToken: localStorage.getItem("authToken") ?? '',
    viewingAsUser: localStorage.getItem(DISPLAY_KEY),
    realUser: null,
    realToken: localStorage.getItem(REAL_TOKEN_KEY) ?? '',
    impersonationExpiresAt: localStorage.getItem(EXPIRES_KEY),
});

/** Begin impersonating `userId`. Calls backend, swaps to the returned token,
 * then re-runs token-login so loggedInUser reflects the target's real
 * permissions. Throws on failure (e.g. trying to impersonate a Super Admin). */
export async function startViewingAs(
    userId: number,
    displayName: string,
    reason: string,
    proof: { password?: string; googleIdToken?: string },
) {
    const service = new ImpersonationService();
    const { token, expires_at } = await service.start(userId, reason, proof);

    // Fetch under the new token BEFORE committing it to the store, so a failure
    // here leaves the admin session intact. Swapping first stranded the app
    // holding a token it could not use: every later request went out as the
    // target and came back 401, including the retry.
    let targetUser: User;
    try {
        const targetInfo = await new LoginService().tokenLogin(token);
        targetUser = targetInfo.user;
    } catch (e) {
        // The server minted a session; close it rather than leave it open for
        // its full five minutes with nobody behind it.
        await service.stopWithToken(token).catch(() => undefined);
        throw e;
    }

    appStore.realUser = { ...appStore.loggedInUser } as User;
    appStore.realToken = appStore.authToken;
    appStore.authToken = token;
    appStore.viewingAsUser = displayName;
    appStore.impersonationExpiresAt = expires_at;
    appStore.loggedInUser = targetUser;
}

/** Restore the admin session locally without calling the backend. Used when
 * the impersonation token has already expired (timer hits 0, or a 401 came
 * back) — calling /stop/ under an expired token would itself 401. */
async function clearImpersonationLocal() {
    const adminToken = appStore.realToken;
    appStore.authToken = adminToken;
    appStore.realToken = '';
    appStore.viewingAsUser = null;
    appStore.impersonationExpiresAt = null;

    if (adminToken) {
        try {
            const adminInfo = await new LoginService().tokenLogin(adminToken);
            appStore.loggedInUser = adminInfo.user;
        } catch (e) {
            console.warn("Failed to restore admin session via token-login", e);
            if (appStore.realUser) appStore.loggedInUser = appStore.realUser;
        }
    } else if (appStore.realUser) {
        appStore.loggedInUser = appStore.realUser;
    }
    appStore.realUser = null;
}

/** Stop impersonating from a user click — revoke the impersonation token
 * server-side, then restore the admin session. */
export async function stopViewingAs() {
    const service = new ImpersonationService();
    try {
        await service.stop();
    } catch (e) {
        console.warn("Impersonation stop failed; restoring local session anyway", e);
    }
    await clearImpersonationLocal();
}

/** Called when the impersonation token has expired (timer hits 0 or a 401
 * came back). Skips the backend revoke since the token is already invalid. */
export async function handleImpersonationExpired() {
    if (!appStore.viewingAsUser) return;
    await clearImpersonationLocal();
}

subscribe(appStore, () => {
    if (appStore.authToken.length > 0) {
        localStorage.setItem("authToken", appStore.authToken);
    } else {
        localStorage.removeItem("authToken");
    }

    if (appStore.realToken.length > 0) {
        localStorage.setItem(REAL_TOKEN_KEY, appStore.realToken);
    } else {
        localStorage.removeItem(REAL_TOKEN_KEY);
    }

    if (appStore.viewingAsUser) {
        localStorage.setItem(DISPLAY_KEY, appStore.viewingAsUser);
    } else {
        localStorage.removeItem(DISPLAY_KEY);
    }

    if (appStore.impersonationExpiresAt) {
        localStorage.setItem(EXPIRES_KEY, appStore.impersonationExpiresAt);
    } else {
        localStorage.removeItem(EXPIRES_KEY);
    }
});
